import { useEffect, useState } from "react";
import { Map, GeoJSON, Layer } from "leaflet";
import { area } from "@turf/turf";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { floor } from "lodash-es";

import {
  DrawType,
  MeasureImpact,
  MeasureConfLayer,
  MeasureConf,
  DrawState,
} from "types/types";

import { latLonToLonLat } from "utils/TurfHelperUtils";
import {
  convertLatLngsToPolygon,
  cutUnderlyingPolygons,
  fitBasePolygon,
} from "utils/DrawUtils";

import { basePolygonLayerStyle } from "config/geometryStyles";

interface Props {
  mapRef: Map;
  selectedMeasure: MeasureImpact;
  measureConfigurations: MeasureConf[];
  basePolygon: GeoJSON;
  minArea: number;
  drawState: DrawState;
  updateDrawState: (drawType: DrawType) => void;
  addNewGeometryToMeasureConfig: (
    newLayer: MeasureConfLayer,
    updatedMeasureConfigs: MeasureConf[]
  ) => void;
}

export default function DrawPolygon(props: Props) {
  const {
    mapRef,
    selectedMeasure,
    measureConfigurations,
    basePolygon,
    minArea,
    drawState,
    updateDrawState,
    addNewGeometryToMeasureConfig,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [uuid, setUuid] = useState<string>(drawState.uuid);

  useEffect(() => {
    return function () {
      //@ts-ignore
      if (!mapRef.pm.Draw.Polygon._enabled) return;
      mapRef.pm.disableDraw("Polygon");
      mapRef.off("pm:create");
    };
  }, [mapRef]);

  if (uuid !== drawState.uuid) {
    // When we remove a polygon we need to reinitialize drawing
    // Listener will otherwise contain previous state
    setUuid(drawState.uuid);
    mapRef.pm.disableDraw("Polygon");
    mapRef.off("pm:drawstart");
    mapRef.off("pm:create");
    mapRef.off("pm:change");
  }

  function cleanUp() {
    updateDrawState("off");

    //@ts-ignore
    if (!mapRef.pm.Draw.Polygon._enabled) return;
    mapRef.pm.disableDraw("Polygon");
    mapRef.off("pm:drawstart");
    mapRef.off("pm:create");
    mapRef.off("pm:change");
  }

  //@ts-ignore
  if (mapRef.pm.Draw.Polygon._enabled) return null;
  if (drawState.drawType !== "create") return null;

  mapRef.on("pm:drawstart", (e) => {
    e.workingLayer.on("pm:change", (ev) => {
      const polygonLatLngs = ev.latlngs;
      const tooltipPane = mapRef.getPanes().tooltipPane;
      // @ts-ignore
      const [firstTooltip] = tooltipPane.getElementsByClassName(
        "leaflet-tooltip-bottom"
      );

      const polygon = convertLatLngsToPolygon(polygonLatLngs);
      if (polygon) {
        const areaSquareMeter = floor(area(polygon), 2).toLocaleString("nl-BE");
        const squareMeterDiv = document.getElementById("squareMeterDiv");
        if (!squareMeterDiv) {
          const newSquareMeterDiv = document.createElement("div");
          newSquareMeterDiv.id = "squareMeterDiv";
          newSquareMeterDiv.textContent = t("label.areaSqM", {
            area: areaSquareMeter,
          });
          firstTooltip.appendChild(newSquareMeterDiv);
        } else {
          squareMeterDiv.textContent = t("label.areaSqM", {
            area: areaSquareMeter,
          });
        }
      }
    });
  });

  function handleCustomError(message: string, layer: Layer) {
    enqueueSnackbar(message, { variant: "error" });
    layer.remove();
    return updateDrawState("create");
  }

  mapRef.on("pm:create", (event) => {
    const { layer: overLayer } = event;
    const cutPolygon = fitBasePolygon(overLayer, basePolygon);

    if (!cutPolygon)
      return handleCustomError(t("label.polygonMustBeInStudyArea"), overLayer);

    const cutPolygonSize = area(cutPolygon);

    if (cutPolygonSize < minArea)
      return handleCustomError(
        t("label.polygonCantBeSmallerThan", { minArea }),
        overLayer
      );

    const updatedMeasureConfiguration = cutUnderlyingPolygons(
      overLayer,
      measureConfigurations,
      minArea
    );

    if (cutPolygon) {
      //@ts-ignore
      overLayer.setLatLngs(
        // @ts-ignore
        latLonToLonLat(cutPolygon.geometry.coordinates)
      );
    }

    addNewGeometryToMeasureConfig(
      {
        id: Date.now() + Math.random(),
        geometryLayer: overLayer,
      },
      updatedMeasureConfiguration
    );

    mapRef.removeLayer(overLayer);

    return cleanUp();
  });

  mapRef.pm.enableDraw("Polygon", {
    finishOn: "dblclick",
    allowSelfIntersection: false,
    snapDistance: 5,
    templineStyle: {
      color: selectedMeasure.measure.color,
    },
    hintlineStyle: {
      color: selectedMeasure.measure.color,
    },
    pathOptions: {
      ...basePolygonLayerStyle,
      color: selectedMeasure.measure.color,
      fillColor: selectedMeasure.measure.color,
    },
  });

  return null;
}
